<script setup lang="ts">
/**
 * Every receipt, and one receipt in full.
 *
 * The list carries OPEN checks alongside settled ones, because "what is sitting
 * on tables right now" and "what did we take" are the same question asked at
 * two moments, and separating them into two screens is how a manager ends up
 * with two numbers that do not add up.
 *
 * A check opens into its lines, its per-rate VAT, its payments and its receipt
 * number — assembled from the same fold the till used, not from a receipt
 * copy stored somewhere.
 */

import { computed } from "vue";
import type { CheckDetail, CheckSummary } from "../api";
import type { Loaded } from "../load";
import { businessDate, clockTime, money, rate } from "../format";

const props = defineProps<{
  checks: readonly CheckSummary[];
  detail: Loaded<CheckDetail> | null;
  /** The venue's trading day: an open check from before it was left open. */
  today?: string;
  timezone: string;
  locale: string;
  t: (key: string) => string;
}>();

defineEmits<{ open: [orderId: string]; close: [] }>();

const sorted = computed(() =>
  [...props.checks].sort((a, b) => {
    // Open tables first — they are the ones somebody can still act on.
    if (a.state !== b.state) return a.state === "OPEN" ? -1 : 1;
    // Newest first, and the RECEIPT NUMBER breaks a tie. Two checks opened in
    // the same minute used to come back in whatever order the fold produced,
    // so a register read out of receipt order — which is the one thing an
    // accountant checks a register for.
    const byTime = b.openedAt.localeCompare(a.openedAt);
    if (byTime !== 0) return byTime;
    return (b.receiptNumber ?? "").localeCompare(a.receiptNumber ?? "");
  }),
);

/**
 * WHICH DAY, when the list covers more than one.
 *
 * On "Posledních 7 dní" every row showed a clock time and nothing else, so a
 * register of 652 receipts spanning a fortnight read as one very long evening.
 * "17:57" answers nothing on its own.
 *
 * Derived from the rows rather than passed in as the range, so it is right by
 * construction: a seven-day range that happens to hold one trading day does
 * not get a column of the same date repeated, and a "today" range that spans
 * the 04:00 cutover — which it does, every night — gets one.
 */
const spansDays = computed(() => new Set(props.checks.map((c) => c.businessDate)).size > 1);

/** "4", "Zahrádka 1", "Účet 3", "Výdej A07" — what the floor says, never "T4", "tab:3" or "pickup:A07". */
const tableLabel = (check: CheckSummary): string => {
  if (check.tableName) return check.tableName;
  const tab = /^tab:(\d+)$/.exec(check.tableRef ?? "");
  if (tab) return props.t("checks.tab").replace("{n}", tab[1]!);
  // A counter sale is called by its number, as the guest was.
  const pickup = /^pickup:([A-Z]\d+)$/.exec(check.tableRef ?? "");
  if (pickup) return props.t("checks.pickup").replace("{code}", pickup[1]!);
  return check.tableRef ?? "—";
};

/** Open since an earlier trading day: left open, and said so with the day. */
const leftOpen = (check: CheckSummary): boolean =>
  check.state === "OPEN" && props.today !== undefined && check.businessDate < props.today;

/** Each form once, in words: two cash payments on one check are "Hotově". */
const tenders = (kinds: readonly string[]): string =>
  [...new Set(kinds)].map((k) => props.t(`tender.${k}`)).join(", ") || "—";

const detailValue = computed(() =>
  props.detail?.status === "ready" ? props.detail.value : null,
);

/**
 * THE ONE FAILURE THAT LOOKS LIKE SUCCESS.
 *
 * A Playground acknowledgement is `REPORTED` with a POK, which is what a
 * successful filing looks like in every field. It discharges nothing. So `test`
 * is checked BEFORE the state, because reading the state first is exactly the
 * mistake that would put a green tick on an unreported sale.
 */
const fiscalOf = computed(() => detailValue.value?.checks[0]?.fiscal ?? null);

const fiscalWord = computed((): string => {
  const f = fiscalOf.value;
  if (!f) return "";
  if (f.test) return props.t("checks.fiscal.test");
  if (f.state === "QUARANTINED") return props.t("checks.fiscal.refused");
  if (f.state === "REPORTED") return props.t("checks.fiscal.reported");
  return props.t("checks.fiscal.pending");
});

const fiscalTone = computed((): string => {
  const f = fiscalOf.value;
  if (!f) return "";
  if (f.state === "QUARANTINED") return "gf-chip--bad";
  // A warning, not a success: it reports nothing.
  if (f.test) return "gf-chip--warn";
  if (f.state === "REPORTED") return f.warnings.length > 0 ? "gf-chip--warn" : "gf-chip--good";
  return "gf-chip--warn";
});

/** What the authority actually said, when it said anything. */
const fiscalDetail = computed((): string => {
  const f = fiscalOf.value;
  if (!f) return "";
  const parts: string[] = [];
  if (f.test) parts.push(props.t("checks.fiscal.testWhy"));
  if (f.code !== null) parts.push(`${String(f.code)} ${f.message ?? ""}`.trim());
  for (const w of f.warnings) parts.push(`${String(w.code)} ${w.message}`);
  if (!f.test && f.state !== "REPORTED" && f.attempts > 0) {
    parts.push(`${props.t("fiscal.attempts")} ${String(f.attempts)}`);
  }
  return parts.join(" · ");
});
</script>

<template>
  <section>
    <div class="gf-panel">
      <div class="gf-scroll">
        <table class="gf-table">
          <thead>
            <tr>
              <th scope="col">{{ t("checks.number") }}</th>
              <th v-if="spansDays" scope="col">{{ t("common.date") }}</th>
              <th scope="col">{{ t("checks.table") }}</th>
              <th scope="col">{{ t("checks.opened") }}</th>
              <th scope="col">{{ t("checks.state") }}</th>
              <th scope="col">{{ t("checks.tenders") }}</th>
              <th scope="col" class="gf-num">{{ t("sales.revenue") }}</th>
              <th scope="col"><span class="gf-sr">{{ t("checks.lines") }}</span></th>
            </tr>
          </thead>
          <tbody>
            <!--
              THE WHOLE ROW OPENS THE CHECK. The "Položky" link stays, for the
              keyboard and a screen reader, but a 23 px link was the only target
              on a row a manager scans with a mouse.
            -->
            <tr
              v-for="check in sorted"
              :key="check.checkId"
              class="checks__row"
              :data-state="check.state"
              @click="$emit('open', check.orderId)"
            >
              <th scope="row">
                <span v-if="check.receiptNumber" class="gf-mono">{{ check.receiptNumber }}</span>
                <span v-else class="checks__noNumber">{{ t("checks.noNumber") }}</span>
              </th>
              <td v-if="spansDays">{{ businessDate(check.businessDate, locale) }}</td>
              <td>{{ tableLabel(check) }}</td>
              <td>{{ clockTime(check.openedAt, timezone, locale) }}</td>
              <td>
                <!--
                  The state is a WORD, not a colour. A colour-blind manager and
                  a printed page both lose a coloured dot.
                -->
                <!--
                  A pill only where a row DEVIATES. "Uzavřený" in a pill on
                  every row of three hundred was noise that hid the two open
                  ones; the ordinary state is plain words.
                -->
                <span v-if="check.state === 'OPEN'" class="gf-chip" :class="leftOpen(check) ? 'gf-chip--bad' : 'gf-chip--warn'">
                  <template v-if="leftOpen(check)">
                    {{ t("checks.openSince").replace("{day}", businessDate(check.businessDate, locale)) }}
                  </template>
                  <template v-else>{{ t("checks.stateOpen") }}</template>
                </span>
                <span v-else class="gf-muted">{{ t("checks.stateSettled") }}</span>
              </td>
              <td class="gf-muted">{{ tenders(check.tenderKinds) }}</td>
              <td class="gf-num gf-strong">{{ money(check.dueMinor, locale) }}</td>
              <td>
                <button type="button" class="gf-link gf-link--quiet checks__open" @click.stop="$emit('open', check.orderId)">
                  {{ t("checks.lines") }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-if="sorted.length === 0" class="gf-quiet">{{ t("common.empty") }}</p>
    </div>

    <div v-if="detail !== null" class="gf-drawer" role="dialog" aria-modal="false">
      <button type="button" class="gf-drawer__close" @click="$emit('close')">×</button>

      <p v-if="detail.status === 'loading'" role="status">{{ t("common.loading") }}</p>
      <p v-else-if="detail.status === 'failed'" role="alert">
        {{ t("common.failed") }} — {{ detail.detail }}
      </p>

      <template v-else-if="detailValue">
        <h2 class="gf-drawer__title">
          {{ detailValue.checks[0]?.receiptNumber ?? t("checks.noNumber") }}
        </h2>

        <!--
          THE RECEIPT AND ITS FISCAL STANDING, SIDE BY SIDE.

          These are one question, not two: a guest holding an účtenka wants to
          know it is real, and an inspector asking about the same sale wants to
          know it was filed. Showing the receipt alone is the friendlier screen
          and the dishonest one.

          The Playground case is the reason the wording matters. A test
          acknowledgement comes back looking exactly like a success and
          discharges no obligation under §16 ZoET, so it is graded as a WARNING
          in its own words — never a tick, never the same colour as a real FIK.
        -->
        <p
          v-if="fiscalOf"
          class="gf-chip"
          :class="fiscalTone"
          :role="fiscalTone === 'gf-chip--bad' ? 'alert' : undefined"
        >
          {{ fiscalWord }}
          <span v-if="fiscalDetail" class="gf-muted">{{ fiscalDetail }}</span>
        </p>
        <p v-else-if="detailValue.checks[0]?.closedAt" class="gf-chip gf-chip--bad" role="alert">
          {{ t("checks.fiscal.none") }}
        </p>

        <table class="gf-table">
          <thead>
            <tr>
              <th scope="col">{{ t("checks.lines") }}</th>
              <th scope="col" class="gf-num">{{ t("menu.rate") }}</th>
              <th scope="col" class="gf-num">{{ t("common.total") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="line in detailValue.lines" :key="line.selectionId" :data-voided="line.voided">
              <th scope="row">
                {{ line.name }}
                <!-- Struck through AND labelled: the strike alone is a style. -->
                <span v-if="line.voided" class="gf-chip gf-chip--bad">
                  {{ t("checks.voided") }}<template v-if="line.voidReason">
                    · {{ t(`voidReason.${line.voidReason}`) }}</template>
                </span>
              </th>
              <td class="gf-num gf-muted">{{ rate(line.rateBp, locale) }}</td>
              <td class="gf-num">{{ money(line.grossMinor, locale) }}</td>
            </tr>
          </tbody>
          <!-- In the line table, so the VAT and the total stand under the lines' own column. -->
          <tfoot class="totals">
            <tr v-for="line in detailValue.checks[0]?.taxLines ?? []" :key="line.rateBp">
              <th scope="row" colspan="2" class="gf-muted">
                {{ t("sales.vat") }} {{ rate(line.rateBp, locale) }}
              </th>
              <td class="gf-num gf-muted">{{ money(line.vatMinor, locale) }}</td>
            </tr>
            <tr v-if="(detailValue.checks[0]?.roundingMinor ?? 0n) !== 0n">
              <th scope="row" colspan="2" class="gf-muted">{{ t("sales.rounding") }}</th>
              <td class="gf-num gf-muted">
                {{ money(detailValue.checks[0]?.roundingMinor ?? 0n, locale) }}
              </td>
            </tr>
            <tr class="totals__due">
              <th scope="row" colspan="2">{{ t("common.total") }}</th>
              <td class="gf-num">{{ money(detailValue.checks[0]?.dueMinor ?? 0n, locale) }}</td>
            </tr>
          </tfoot>
        </table>

        <!-- An open check has taken nothing yet: no heading over an empty list. -->
        <h3 v-if="detailValue.tenders.length > 0" class="gf-drawer__sub">{{ t("checks.tenders") }}</h3>
        <ul v-if="detailValue.tenders.length > 0" class="gf-list">
          <li v-for="tender in detailValue.tenders" :key="tender.tenderId">
            {{ t(`tender.${tender.kind}`) }} — {{ money(tender.amountMinor, locale) }}
            <span v-if="tender.voided" class="gf-chip gf-chip--bad">{{ t("checks.voided") }}</span>
          </li>
        </ul>

        <template v-if="detailValue.anomalies.length > 0">
          <h3 class="gf-drawer__sub">{{ t("checks.anomalies") }}</h3>
          <ul class="gf-list gf-list--warn">
            <li v-for="(a, i) in detailValue.anomalies" :key="i">{{ a.kind }} — {{ a.detail }}</li>
          </ul>
        </template>
      </template>
    </div>
  </section>
</template>

<style scoped>
/*
 * The EET notice is a sentence, not a tag: it wraps inside the panel. As a
 * pill (inline-flex, fully rounded) a two-line sentence became two columns in
 * a lozenge, so here it is a tinted callout block instead.
 */
.gf-drawer p.gf-chip {
  display: block;
  margin: 0 0 var(--gf-space-3);
  padding: var(--gf-space-2) var(--gf-space-3);
  white-space: normal;
  line-height: var(--gf-leading-normal);
  border-radius: var(--gf-radius-md);
}

.totals th,
.totals td {
  border-top: 0;
  padding-block: var(--gf-space-1);
  font-weight: var(--gf-weight-normal);
}

.totals tr:first-child th,
.totals tr:first-child td {
  border-top: var(--gf-border-thin) solid var(--gf-color-border-subtle);
  padding-top: var(--gf-space-3);
}

.totals__due th,
.totals__due td {
  font-weight: var(--gf-weight-semibold);
}

.checks__row {
  cursor: pointer;
}

/* The status column says it is open; the number column says what it lacks. */
.checks__noNumber {
  font-style: italic;
  font-weight: var(--gf-weight-normal);
  color: var(--gf-color-text-muted);
}

.checks__open {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
}

tr[data-voided="true"] th[scope="row"] {
  text-decoration: line-through;
  color: var(--gf-color-text-muted);
}
</style>
